// Auto-refresh support for visuals ("Grafana-style" live charts).
//
// Every visual type exposes an `autoRefreshSeconds` catalog option (0 = off).
// The value rides to every render surface inside the payload it already
// receives (`optionsArr` / `visualLayout.options`), so honoring it is purely a
// client concern: read the option, then silently re-fetch on the interval.

import { useEffect, useRef } from 'react'
import type { VisualLayout, VisualOption } from './types'

// Floor for the poll cadence — protects the data DB from a mistyped "0.1".
const MIN_REFRESH_SECONDS = 2

/**
 * Read the effective auto-refresh interval (seconds) from a visual's options.
 * Accepts the payload's `optionsArr` and/or `visualLayout` (options checked in
 * that order); returns 0 when the option is absent, unparsable, or ≤ 0.
 */
export function readAutoRefreshSeconds(
  optionsArr?: VisualOption[] | null,
  visualLayout?: VisualLayout | null,
): number {
  const lists = [optionsArr, visualLayout?.options]
  for (const opts of lists) {
    if (!Array.isArray(opts)) continue
    const hit = opts.find(
      (o) => String(o?.optionName || '').toLowerCase() === 'autorefreshseconds',
    )
    if (!hit) continue
    const n = Number(hit.defaultValue)
    if (!Number.isFinite(n) || n <= 0) return 0
    return Math.max(MIN_REFRESH_SECONDS, Math.round(n))
  }
  return 0
}

/**
 * Fire `tick` every `intervalSeconds` (0 disables). Ticks are skipped while
 * the document is hidden (a refresh runs immediately on return so the chart
 * isn't stale) and while a previous async tick is still in flight — so a slow
 * query can never stack requests. The caller's tick should re-fetch WITHOUT
 * clearing what's on screen (silent refresh, no loading flash).
 */
export function useAutoRefresh(
  intervalSeconds: number,
  tick: () => void | Promise<void>,
): void {
  const tickRef = useRef(tick)
  tickRef.current = tick
  const busyRef = useRef(false)

  useEffect(() => {
    if (!intervalSeconds || intervalSeconds <= 0) return
    const periodMs = Math.max(MIN_REFRESH_SECONDS, intervalSeconds) * 1000
    let missedWhileHidden = false

    const fire = () => {
      if (busyRef.current) return
      busyRef.current = true
      Promise.resolve()
        .then(() => tickRef.current())
        .catch(() => { /* a failed poll keeps the last good data on screen */ })
        .finally(() => { busyRef.current = false })
    }

    const onTick = () => {
      if (document.visibilityState === 'hidden') { missedWhileHidden = true; return }
      fire()
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible' && missedWhileHidden) {
        missedWhileHidden = false
        fire()
      }
    }

    const timer = window.setInterval(onTick, periodMs)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [intervalSeconds])
}
